import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaArrowRight, FaSearch } from 'react-icons/fa';
import programsData from '../programsData';
import { TimeContext } from '../TimeContext';
import { trackProgramSearch } from '../analytics/events';
import './Homepage.css';
import { SiteHeader, SiteFooter } from './Homepage';

const Programs = () => {
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => { setPrograms(programsData); }, []);

  const navLinks = [
    { to:'/',            label:t('nav.home','Home'),        ariaLabel:t('nav.home','Home') },
    { to:'/programs',    label:t('nav.programs','Programs'),ariaLabel:t('nav.programs','Programs') },
    { to:'/campus-life', label:t('nav.campusLife','Campus Life'), ariaLabel:t('nav.campusLife','Campus Life') },
    { to:'/alumni',      label:t('nav.alumni','Alumni'),    ariaLabel:t('nav.alumni','Alumni') },
    { to:'/enrollment',  label:t('nav.enrollment','Enrollment'), ariaLabel:t('nav.enrollment','Enrollment') },
  ];

  const departments = [
    'All',
    'Travel and Tourism',
    'Hospitality, Hotel and Catering',
    'Business',
    'Languages',
    'International College/University Entry Exams',
    'Computer',
  ];

  const filteredPrograms = programs
    .filter(program => {
      if (filter === 'All') return true;
      if (filter === 'Travel and Tourism') return program.id <= 5;
      if (filter === 'Hospitality, Hotel and Catering') return program.id >= 6 && program.id <= 14;
      if (filter === 'Business') return program.id >= 15 && program.id <= 30;
      if (filter === 'Languages') return program.id >= 31 && program.id <= 41;
      if (filter === 'International College/University Entry Exams') return program.id >= 42 && program.id <= 47;
      if (filter === 'Computer') return program.id >= 48;
      return true;
    })
    .filter(p =>
      !search.trim() ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description||'').toLowerCase().includes(search.toLowerCase())
    );

  /* Report catalogue searches once the visitor stops typing.
     Debounced so a 12-letter query is one event, not twelve, and
     capped at course names - never anything a visitor types about
     themselves. */
  const searchTimer = useRef(null);
  useEffect(() => {
    if (!search.trim()) return undefined;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      trackProgramSearch(search, filteredPrograms.length);
    }, 900);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const deptShort = dept => {
    const map = {
      'All': 'All',
      'Travel and Tourism': 'Travel & Tourism',
      'Hospitality, Hotel and Catering': 'Hospitality',
      'Business': 'Business',
      'Languages': 'Languages',
      'International College/University Entry Exams': 'Int\'l Exams',
      'Computer': 'Computer',
    };
    return map[dept] || dept;
  };

  const deptColors = {
    'Travel and Tourism': '#4169E1',
    'Hospitality, Hotel and Catering': '#D97706',
    'Business': '#1E3A8A',
    'Languages': '#5a9200',
    'International College/University Entry Exams': '#7C3AED',
    'Computer': '#0891B2',
  };

  const getCardDept = p => {
    if (p.id <= 5) return 'Travel and Tourism';
    if (p.id <= 14) return 'Hospitality, Hotel and Catering';
    if (p.id <= 30) return 'Business';
    if (p.id <= 41) return 'Languages';
    if (p.id <= 47) return 'International College/University Entry Exams';
    return 'Computer';
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{t('seo.title','Programs - San Tomas Tourism College')}</title>
        <meta name="description" content={t('seo.description','Explore the diverse programs offered by San Tomas Tourism College in Nairobi.')} />
        <meta name="keywords" content={t('seo.keywords','San Tomas Tourism College, Nairobi programs, tourism courses, hospitality training, business diploma, language courses, IT certification')} />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        <link rel="canonical" href="https://santomastourismcollege.com/programs" />
        <meta property="og:title" content="Programs - San Tomas Tourism College" />
        <meta property="og:image" content="/social-banner.jpg" />
      </Helmet>

      <a href="#main-content" className="sr-only focus:not-sr-only">{t('a11y.skipToContent','Skip to main content')}</a>

      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      {/* ── PAGE HERO ── */}
      <section className="sttc-hero" style={{ minHeight:'40vh', alignItems:'flex-end', paddingBottom:0 }}>
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-geo" aria-hidden="true" />
        <div className="hero-circle-1" aria-hidden="true" />
        <div className="sttc-container" style={{ position:'relative', zIndex:2, paddingTop:120, paddingBottom:56 }}>
          <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}>
            <div className="section-eyebrow" style={{ color:'rgba(251,191,36,0.9)' }}>
              {programs.length} Programs Available
            </div>
            <h1 className="section-title" style={{ color:'#fff', fontSize:'clamp(36px,5vw,60px)' }}>
              Explore Our <em style={{ color:'#FBBF24' }}>Programs</em>
            </h1>
            <p style={{ fontSize:15, color:'rgba(255,255,255,0.78)', maxWidth:520, lineHeight:1.8, fontWeight:300, marginTop:12 }}>
              50+ world-class courses spanning Tourism, Hospitality, Business, Languages, and IT.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── FILTER BAR ── */}
      <div className="filter-bar" role="navigation" aria-label="Filter programs by department">
        <div className="filter-bar-inner">
          {departments.map(dept => (
            <button
              key={dept}
              className={`filter-btn${filter === dept ? ' active' : ''}`}
              onClick={() => setFilter(dept)}
              aria-pressed={filter === dept}
            >
              {deptShort(dept)}
              {dept !== 'All' && (
                <span style={{ marginLeft:4, fontSize:10, opacity:0.7 }}>
                  ({programs.filter(p => getCardDept(p) === dept).length})
                </span>
              )}
              {dept === 'All' && (
                <span style={{ marginLeft:4, fontSize:10, opacity:0.7 }}>({programs.length})</span>
              )}
            </button>
          ))}
          {/* Search */}
          <div style={{ position:'relative', marginLeft:'auto' }}>
            <FaSearch style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', fontSize:12 }} />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search programs…"
              className="admin-search"
              aria-label="Search programs"
            />
          </div>
        </div>
      </div>

      {/* ── PROGRAMS GRID ── */}
      <section id="main-content" className="sttc-section tinted">
        <div className="sttc-container">
          {filteredPrograms.length === 0 ? (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>🔍</div>
              <p style={{ fontSize:18, color:'#6B7280' }}>No programs match your search.</p>
              <button onClick={() => { setFilter('All'); setSearch(''); }} className="btn-outline" style={{ marginTop:20 }}>Clear Filters</button>
            </div>
          ) : (
            <>
              <p style={{ fontSize:13, color:'#6B7280', marginBottom:32, fontWeight:500 }}>
                Showing {filteredPrograms.length} program{filteredPrograms.length !== 1 ? 's' : ''}
                {filter !== 'All' ? ` in ${deptShort(filter)}` : ''}
                {search ? ` matching "${search}"` : ''}
              </p>
              <div className="programs-grid">
                {filteredPrograms.map((program, i) => {
                  const dept = getCardDept(program);
                  const deptColor = deptColors[dept] || '#4169E1';
                  return (
                    <motion.div
                      key={program.id}
                      className="program-card"
                      initial={{ opacity:0, y:40 }}
                      whileInView={{ opacity:1, y:0 }}
                      viewport={{ once:true, margin:'-50px' }}
                      transition={{ duration:0.5, delay:(i%6)*0.06 }}
                      tabIndex="0"
                    >
                      <div className="program-card-header" style={{ background:`linear-gradient(135deg, ${deptColor}, #0F2A70)` }}>
                        <div className="program-card-icon">
                          <img
                            src={`https://picsum.photos/32/32?random=${program.id}`}
                            alt={`${program.name} icon`}
                            loading="lazy" decoding="async"
                            onError={e => e.target.parentNode.classList.add('hidden')}
                          />
                        </div>
                        <div>
                          <h3>{program.name}</h3>
                          <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)', marginTop:2 }}>{deptShort(dept)}</div>
                        </div>
                      </div>
                      <div className="program-card-body">
                        <p>{program.description}</p>
                        <div className="program-duration">
                          {t('courses.duration','Duration:')} {program.duration}
                        </div>
                        {program.feePerLevel && (
                          <div style={{ fontSize:12, color:'#6B7280', marginBottom:12 }}>
                            <span style={{ color:'#4169E1', fontWeight:600 }}>Fee/Level:</span> Ksh {program.feePerLevel?.toLocaleString()}
                          </div>
                        )}
                        {program.feePerTerm && (
                          <div style={{ fontSize:12, color:'#6B7280', marginBottom:12 }}>
                            <span style={{ color:'#4169E1', fontWeight:600 }}>Fee/Term:</span> Ksh {program.feePerTerm?.toLocaleString()}
                          </div>
                        )}
                        <div style={{ display:'flex', gap:10, marginTop:'auto', paddingTop:14, borderTop:'1px solid rgba(65,105,225,0.08)', flexWrap:'wrap' }}>
                          <Link
                            to={`/programs/${program.id}`}
                            className="program-card-link"
                            style={{ flex:1 }}
                            aria-label={`Learn more about ${program.name}`}
                          >
                            Learn more <FaArrowRight />
                          </Link>
                          <Link
                            to="/enrollment"
                            className="tag amber"
                            style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:4 }}
                          >
                            Apply
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}

          {/* CTA */}
          <div style={{ textAlign:'center', marginTop:64, padding:48, background:'linear-gradient(135deg, #1E3A8A, #4169E1)', borderRadius:16 }}>
            <div className="section-eyebrow" style={{ color:'rgba(251,191,36,0.9)', justifyContent:'center' }}>Ready to Begin?</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:'clamp(24px,3vw,36px)', color:'#fff', fontWeight:900, marginBottom:16 }}>
              Apply for the <span style={{ color:'#FBBF24', fontStyle:'italic' }}>Next Intake</span>
            </h2>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.75)', marginBottom:28, maxWidth:480, margin:'0 auto 28px' }}>
              Applications are now open. Secure your place in Kenya's leading tourism college.
            </p>
            <Link to="/enrollment" className="btn-primary" aria-label="Apply to San Tomas Tourism College">
              <span>Apply Now</span><FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default Programs;